import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import ReviewForm from '../components/Review/ReviewForm';
import styles from '../styles/ReviewPage.module.css';

const ReviewPage = () => {
  const { songId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [songData, setSongData] = useState(null);
  const [pageError, setPageError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setPageError('Please log in to review your song');
      navigate('/login');
      return;
    }
    fetchSongData();
  }, [songId, currentUser, navigate]);

  const fetchSongData = async () => {
    setLoading(true);
    setPageError(null);

    try {
      const orderDocRef = doc(db, 'orders', songId);
      const orderDoc = await getDoc(orderDocRef);

      console.log('ORDER DOCUMENT FETCH', {
        documentId: songId,
        exists: orderDoc.exists(),
        data: orderDoc.exists() ? orderDoc.data() : null,
        error: orderDoc.exists() ? null : 'Order document not found',
      });

      let foundOrder = null;
      if (orderDoc.exists()) {
        foundOrder = { id: orderDoc.id, ...orderDoc.data() };
      } else {
        const ordersRef = collection(db, 'orders');
        const q = query(
          ordersRef,
          where('orderNumber', '==', songId),
          where('userId', '==', currentUser.uid)
        );
        const querySnapshot = await getDocs(q);

        console.log('ORDER NUMBER QUERY', {
          orderNumber: songId,
          userId: currentUser.uid,
          empty: querySnapshot.empty,
          size: querySnapshot.size,
          docs: querySnapshot.docs.map(doc => ({
            id: doc.id,
            userId: doc.data().userId,
            orderNumber: doc.data().orderNumber,
          })),
        });

        if (querySnapshot.empty) {
          console.log('No matching order found by order number for user');
          setPageError('No order found matching your order number');
          navigate('/dashboard');
          return;
        }

        const matchingOrder = querySnapshot.docs[0];
        foundOrder = { id: matchingOrder.id, ...matchingOrder.data() };
      }

      if (!foundOrder) {
        console.log('NO MATCHING ORDER FOUND');
        setPageError('Order not found or not owned by user');
        navigate('/dashboard');
        return;
      }

      console.log('OWNERSHIP VERIFICATION', {
        orderUserId: foundOrder.userId,
        currentUserId: currentUser.uid,
        orderNumber: foundOrder.orderNumber,
      });

      if (foundOrder.userId !== currentUser.uid) {
        console.log('USER DOES NOT OWN ORDER');
        setPageError('You do not have permission to review this order');
        navigate('/dashboard');
        return;
      }

      const userRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userRef);
      let userName = 'Customer';
      if (userDoc.exists()) {
        const userData = userDoc.data();
        userName = userData.name || userData.displayName || userData.email.split('@')[0];
      }

      const enhancedOrderData = {
        id: foundOrder.id,
        orderNumber: foundOrder.orderNumber || songId,
        customerInfo: {
          fullName: userName,
          email: currentUser.email || '',
        },
        songs: foundOrder.songs || [{ name: foundOrder.songFileName || 'Your Song', url: foundOrder.songUrl || '', isMainVersion: true }],
        songFileName: foundOrder.songFileName || (foundOrder.songs && foundOrder.songs.length > 0 ? foundOrder.songs[0].name : 'Your Song'),
        recipientName: foundOrder.recipientName || userName,
        genres: foundOrder.genres || [],
        package: foundOrder.package || '',
        occasion: foundOrder.occasion || foundOrder.details?.occasion || '',
      };

      console.log('Enhanced order data:', enhancedOrderData);
      setSongData(enhancedOrderData);
    } catch (error) {
      console.error('ACCESS CHECK ERROR', {
        message: error.message,
        code: error.code,
        stack: error.stack,
        songId: songId,
        userId: currentUser.uid,
      });
      setPageError(`Error accessing order: ${error.message}`);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className={styles.loading}>Loading...</div>;
  if (pageError) return <div className={styles.error}>Error: {pageError}</div>;
  if (!songData) return null;

  return (
    <div className={styles.reviewPage}>
      <ReviewForm initialSongDetails={songData} />
    </div>
  );
};

export default ReviewPage;